import API_BASE_URL from 'src/constants';
import { getToken, handleUnauthorized } from './authToken';

export interface ConversationMeta {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
  hasFiles: boolean;
}

interface SummaryDto {
  client_key: string;
  title: string;
  updated_at: string;
  message_count: number;
  has_files: boolean;
}

const toMeta = (dto: SummaryDto): ConversationMeta => ({
  id: dto.client_key,
  title: dto.title,
  updatedAt: dto.updated_at,
  messageCount: dto.message_count,
  hasFiles: dto.has_files,
});

const request = async (path: string, init: RequestInit = {}): Promise<Response> => {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}portal/ai_chat/conversations${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (response.status === 401) handleUnauthorized(token);
  if (!response.ok) throw new Error(`Chat history request failed (${response.status})`);
  return response;
};

export async function listConversations(): Promise<ConversationMeta[]> {
  const body = (await (await request('')).json()) as SummaryDto[];
  return body.map(toMeta);
}

export async function getConversationMessages<T>(id: string): Promise<T[]> {
  const body = (await (await request(`/${encodeURIComponent(id)}`)).json()) as SummaryDto & { messages: T[] };
  return body.messages;
}

export async function saveConversation<T>(id: string, title: string, messages: T[]): Promise<ConversationMeta> {
  const body = (await (
    await request(`/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ title, messages }) })
  ).json()) as SummaryDto;
  return toMeta(body);
}

export async function deleteConversation(id: string): Promise<void> {
  await request(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function sendMessageFeedback(
  id: string,
  messageIndex: number,
  feedback: 'up' | 'down' | null,
): Promise<void> {
  await request(`/${encodeURIComponent(id)}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ message_index: messageIndex, feedback }),
  });
}
